import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Calendar, Newspaper, TrendingUp, TrendingDown, Minus, ExternalLink, ArrowRight, ChevronRight, MapPin, Clock, Sparkles } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import PlayerDetailModal from '../components/PlayerDetailModal';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const t = (k, o) => i18n.t(k, o);
const lang = () => (i18n.language || 'fr').slice(0, 2);
const loc = () => ({ fr: 'fr-FR', en: 'en-GB', es: 'es-ES', it: 'it-IT' }[lang()] || 'fr-FR');

const TABS = [
  { id: 'rankings', label: 'padpro.tab_rankings', icon: Trophy },
  { id: 'tournaments', label: 'padpro.tab_calendar', icon: Calendar },
  { id: 'news', label: 'padpro.tab_news', icon: Newspaper },
];

const MoveIcon = ({ move }) => {
  if (move > 0) return <span className="inline-flex items-center gap-0.5 text-[#32CD32] text-xs font-bold"><TrendingUp className="w-3 h-3" />{move}</span>;
  if (move < 0) return <span className="inline-flex items-center gap-0.5 text-red-500 text-xs font-bold"><TrendingDown className="w-3 h-3" />{Math.abs(move)}</span>;
  return <span className="inline-flex items-center gap-0.5 text-[#94A3B8] text-xs font-medium"><Minus className="w-3 h-3" />0</span>;
};

const PlayerCard = ({ player, idx, onClick }) => {
  const isPodium = player.rank <= 3;
  return (
    <button
      type="button"
      onClick={() => onClick(player)}
      className={'relative text-left bg-white rounded-2xl border transition-all hover:-translate-y-1 hover:shadow-xl overflow-hidden group w-full ' +
        (isPodium ? 'border-amber-200 shadow-md' : 'border-[#E8F4FD] shadow-sm')}
      data-testid={`player-card-${player.player_id}`}
    >
      {/* Rank badge */}
      <div className={'absolute top-3 left-3 z-10 w-10 h-10 rounded-xl flex items-center justify-center font-black text-base shadow-lg ' +
        (player.rank === 1 ? 'bg-amber-400 text-white' :
          player.rank === 2 ? 'bg-gray-300 text-white' :
            player.rank === 3 ? 'bg-amber-700 text-white' :
              'bg-[#033871] text-white')}>
        {player.rank}
      </div>

      {/* Move indicator */}
      <div className="absolute top-3 right-3 z-10">
        <MoveIcon move={player.move || 0} />
      </div>

      {/* Player photo - rounded square hero */}
      <div className={'relative h-44 ' + (isPodium ? 'bg-gradient-to-br from-amber-50 to-orange-50' : 'bg-gradient-to-br from-[#F0F7FA] to-[#E8F4FD]')}>
        {player.thumbnail ? (
          <img
            src={player.thumbnail}
            alt={player.full_name}
            className="absolute inset-0 w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-300"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-5xl font-black text-[#033871]/30">
            {player.surname[0]}
          </div>
        )}
        {/* Hover hint */}
        <div className="absolute inset-0 bg-[#033871]/0 group-hover:bg-[#033871]/15 transition-colors flex items-end justify-center pb-2 opacity-0 group-hover:opacity-100">
          <span className="text-[10px] font-bold uppercase tracking-wide text-white bg-[#033871]/80 backdrop-blur px-2 py-0.5 rounded-md">
            {t('padpro.player_sheet')}
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0 flex-1">
            <p className="text-xs text-[#5A8BA8] uppercase tracking-wide font-bold leading-none">{player.name}</p>
            <h3 className="text-base font-black text-[#033871] leading-tight mt-0.5 truncate">{player.surname}</h3>
          </div>
          {player.country_flag && (
            <img src={player.country_flag} alt={player.nationality} className="w-7 h-7 rounded-full object-cover border border-[#E8F4FD] shrink-0" />
          )}
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-[#F0F7FA]">
          <span className="text-[10px] text-[#5A8BA8] uppercase font-bold">{player.country_name || player.nationality}</span>
          <span className="text-sm font-black text-[#0C63BF]">{player.points?.toLocaleString(loc())} pts</span>
        </div>
      </div>
    </button>
  );
};

const CATEGORY_BADGE_COLORS = {
  "FIP-TOUR-BRONZE": "bg-amber-700 text-white",
  "FIP-TOUR-SILVER": "bg-gray-400 text-white",
  "FIP-TOUR-GOLD": "bg-amber-400 text-white",
  "FIP-TOUR-PLATINUM": "bg-slate-700 text-white",
  "Cupra FIP Tour": "bg-[#0C63BF] text-white",
  "FIP WORLD CUP": "bg-purple-600 text-white",
  "FIP BEYOND": "bg-pink-600 text-white",
};

const PREMIER_BADGE_COLORS = {
  "Premier Padel Major": "bg-gradient-to-r from-amber-500 to-orange-500 text-white",
  "Premier Padel P1": "bg-gradient-to-r from-slate-700 to-slate-500 text-white",
  "Premier Padel P2": "bg-gradient-to-r from-[#0C63BF] to-[#033871] text-white",
  "Premier Padel Finals": "bg-gradient-to-r from-purple-700 to-pink-600 text-white",
};


/**
 * Compact horizontal row used in the new "Calendar mode" timeline view.
 * - Left:   day + month pill (badge style)
 * - Middle: tournament name, label badge, country/city
 * - Right:  CTA arrow + (past) trophy / live indicator
 */
const CalendarRow = ({ tournament, past = false, onClick }) => {
  const cat = tournament.premier_label || 'Premier Padel';
  const badgeColor = PREMIER_BADGE_COLORS[cat] || 'bg-[#033871] text-white';

  let dayLabel = '?';
  let monthLabel = '';
  if (tournament.start_date) {
    const d = new Date(tournament.start_date);
    dayLabel = d.getDate();
    monthLabel = d.toLocaleDateString(loc(), { month: 'short' }).replace('.', '');
  }
  const rangeLabel = (tournament.start_date && tournament.end_date)
    ? t('padpro.range', { start: new Date(tournament.start_date).toLocaleDateString(loc(), { day: 'numeric', month: 'long' }), end: new Date(tournament.end_date).toLocaleDateString(loc(), { day: 'numeric', month: 'long', year: 'numeric' }) })
    : t('padpro.dates_tbc');

  return (
    <button
      type="button"
      onClick={() => onClick(tournament)}
      className={'group relative w-full text-left bg-white border rounded-xl overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-lg ' +
        (past ? 'border-amber-100 hover:border-amber-300' : 'border-[#E8F4FD] hover:border-[#0C63BF]')}
      data-testid={`tournament-${tournament.id}`}
    >
      <div className="flex items-stretch gap-0">
        {/* Date pill */}
        <div className={'shrink-0 w-16 sm:w-20 flex flex-col items-center justify-center py-2 text-white relative ' +
          (past ? 'bg-gradient-to-br from-amber-500 to-amber-600' : 'bg-gradient-to-br from-[#033871] to-[#0C63BF]')}>
          <span className="text-2xl sm:text-3xl font-black leading-none">{dayLabel}</span>
          <span className="text-[10px] uppercase tracking-wider font-bold opacity-90 mt-0.5">{monthLabel}</span>
          {past && (
            <Trophy className="absolute -bottom-1 -right-1 w-3.5 h-3.5 text-amber-700 bg-amber-200 rounded-full p-0.5 shadow" />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0 p-3 sm:p-4">
          <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <span className={'inline-flex items-center px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] font-black uppercase tracking-wide ' + badgeColor}>
              {cat}
            </span>
            {tournament.genders && tournament.genders.length > 0 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-[#F0F7FA] rounded-md text-[9px] text-[#5A8BA8] font-medium">
                {tournament.genders.map(g => g === 'Male' ? '♂' : g === 'Female' ? '♀' : g === 'Mixed' ? '⚥' : g).join('')}
              </span>
            )}
            {past && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-md text-[9px] font-bold uppercase">{t('padpro.finished')}</span>
            )}
          </div>
          <h3 className="text-sm sm:text-base font-black text-[#033871] leading-tight truncate" data-testid={`tournament-title-${tournament.id}`}>
            {tournament.title}
          </h3>
          <p className="text-[11px] sm:text-xs text-[#5A8BA8] mt-0.5 truncate">
            {(tournament.city || tournament.country_main) ? (
              <><MapPin className="inline w-3 h-3 mr-0.5 -mt-0.5" />{tournament.city || ''}{tournament.city && tournament.country_main ? ', ' : ''}{tournament.country_main || ''}</>
            ) : (
              <span className="italic">{t('padpro.place_tbc')}</span>
            )}
          </p>
          <p className="hidden sm:block text-[10px] text-[#94A3B8] mt-1">{rangeLabel}</p>
        </div>

        {/* CTA */}
        <div className="hidden sm:flex shrink-0 items-center pr-4 pl-2">
          <span className={'inline-flex items-center gap-1 text-[11px] font-bold transition-all group-hover:gap-2 ' + (past ? 'text-amber-600' : 'text-[#0C63BF]')}>
            {past ? t('padpro.see_results') : t('padpro.details')}
            <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </button>
  );
};

/**
 * Groups a list of tournaments by their starting month and renders a
 * "Mois Année" header followed by a vertical list of ``CalendarRow``s.
 */
const MonthGroup = ({ monthKey, items, past, onClick }) => {
  if (!items.length) return null;
  const [year, month] = monthKey.split('-');
  const m = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1).toLocaleDateString(loc(), { month: 'long' });
  const label = `${m.charAt(0).toUpperCase()}${m.slice(1)} ${year}`;
  return (
    <div className="mb-6" data-testid={`month-group-${monthKey}`}>
      <div className="flex items-center gap-3 mb-3">
        <h3 className="text-xs sm:text-sm font-black uppercase tracking-widest text-[#033871]">{label}</h3>
        <div className="flex-1 h-px bg-gradient-to-r from-[#0C63BF]/40 to-transparent" />
        <span className="text-[10px] text-[#5A8BA8] font-bold">{t('padpro.tournaments_count', { count: items.length })}</span>
      </div>
      <div className="space-y-2 sm:space-y-2.5">
        {items.map(it => <CalendarRow key={it.id} tournament={it} past={past} onClick={onClick} />)}
      </div>
    </div>
  );
};

const groupByMonth = (list) => {
  const map = new Map();
  list.forEach(t => {
    const key = t.start_date ? t.start_date.slice(0, 7) : '0000-00';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(t);
  });
  // sort entries by key (ascending for upcoming, descending applied externally)
  return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
};

const TournamentCard = ({ tournament, past = false, onClick }) => {
  const cat = tournament.category_main || "FIP Event";
  const catColor = CATEGORY_BADGE_COLORS[cat] || "bg-[#033871] text-white";
  const formatRange = () => {
    if (tournament.start_date && tournament.end_date) {
      const s = new Date(tournament.start_date);
      const e = new Date(tournament.end_date);
      const sm = s.toLocaleDateString(loc(), { day: 'numeric', month: 'short' });
      const em = e.toLocaleDateString(loc(), { day: 'numeric', month: 'short', year: 'numeric' });
      return `${sm} – ${em}`;
    }
    if (tournament.publish_date) {
      return new Date(tournament.publish_date).toLocaleDateString(loc(), { day: 'numeric', month: 'short', year: 'numeric' });
    }
    return null;
  };
  return (
    <button
      onClick={() => onClick(tournament)}
      className="text-left bg-white rounded-2xl border border-[#E8F4FD] hover:border-[#0C63BF] hover:shadow-xl hover:-translate-y-1 transition-all overflow-hidden group w-full"
      data-testid={`tournament-${tournament.id}`}
    >
      <div className="aspect-[16/9] overflow-hidden bg-gradient-to-br from-[#033871] to-[#0C63BF] relative">
        {tournament.image ? (
          <img
            src={tournament.image}
            alt={tournament.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Trophy className="w-14 h-14 text-white/30" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
        {/* Top badges */}
        <div className="absolute top-2 left-2 flex gap-1.5 flex-wrap">
          <span className={'px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide ' + catColor}>
            {cat}
          </span>
          {past ? (
            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase bg-amber-400 text-[#033871] flex items-center gap-1">
              <Trophy className="w-3 h-3" /> {t('padpro.finished')}
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase bg-[#32CD32] text-white">
              {t('padpro.upcoming')}
            </span>
          )}
        </div>
        {tournament.country_main && (
          <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md text-[10px] font-bold bg-white/90 backdrop-blur text-[#033871]">
            {tournament.country_main}
          </div>
        )}
        {/* Big winner ribbon for past tournaments */}
        {past && (
          <div className="absolute bottom-12 right-2 flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-400/95 text-[#033871] text-[10px] font-black uppercase tracking-wide shadow-md">
            <Trophy className="w-3 h-3" /> {t('padpro.results')}
          </div>
        )}
        {/* Title overlay */}
        <h3 className="absolute bottom-3 left-3 right-3 text-base sm:text-lg font-black text-white leading-tight line-clamp-2 drop-shadow-md">
          {tournament.title}
        </h3>
      </div>
      <div className={'p-4 flex items-center gap-3 text-xs ' + (past ? 'bg-amber-50/60' : '')}>
        <span className="inline-flex items-center gap-1 text-[#033871] font-semibold">
          <Calendar className="w-3.5 h-3.5 text-[#0C63BF]" />
          {formatRange() || t('padpro.date_tbc')}
        </span>
        {tournament.genders && tournament.genders.length > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#F0F7FA] rounded-md text-[10px] text-[#5A8BA8] font-medium">
            {tournament.genders.map(g => g === 'Male' ? '♂' : g === 'Female' ? '♀' : g === 'Mixed' ? '⚥' : g).join(' ')}
          </span>
        )}
        <span className={'inline-flex items-center gap-1 ml-auto text-[11px] font-bold ' + (past ? 'text-amber-600' : 'text-[#0C63BF]')}>
          {past ? t('padpro.see_results') : t('padpro.details')}
          <ArrowRight className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
        </span>
      </div>
    </button>
  );
};

const ResultsSection = ({ slug }) => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gender, setGender] = useState('men');

  useEffect(() => {
    axios.get(`${API}/padpro/tournaments/${slug}/results`)
      .then(res => setResults(res.data.data))
      .catch(() => setResults(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="mb-5 flex items-center gap-2 text-xs text-[#5A8BA8] px-1" data-testid="results-loading">
      <span className="w-3.5 h-3.5 border-2 border-[#0C63BF] border-t-transparent rounded-full animate-spin" /> {t('padpro.loading_scores')}
    </div>
  );
  const matches = results?.[gender] || [];
  if (!results || (!results.men?.length && !results.women?.length)) return null;

  return (
    <div className="mb-5 rounded-2xl border border-[#E8F4FD] overflow-hidden" data-testid="tournament-results">
      <div className="px-4 py-2.5 bg-[#033871] text-white flex items-center justify-between">
        <p className="text-xs font-black uppercase tracking-wide">{t('padpro.scores')}</p>
        <div className="flex gap-1">
          {[['men', t('padpro.men')], ['women', t('padpro.women')]].map(([g, label]) => (
            <button key={g} onClick={() => setGender(g)}
              className={'px-2.5 py-1 rounded-md text-[10px] font-bold uppercase transition-colors ' + (gender === g ? 'bg-[#32CD32] text-white' : 'bg-white/15 text-white/80 hover:bg-white/25')}
              data-testid={`results-tab-${g}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="divide-y divide-[#F0F7FA]">
        {matches.length === 0 && <p className="text-xs text-[#5A8BA8] text-center py-4">{t('padpro.no_results')}</p>}
        {matches.map((m, i) => (
          <div key={i} className="px-4 py-2.5" data-testid={`result-match-${i}`}>
            <p className="text-[10px] uppercase tracking-wide font-black text-amber-600 mb-1">{m.round_label}</p>
            {m.teams.map((team, ti) => {
              const won = m.winner === ti + 1;
              return (
                <div key={ti} className={'flex items-center justify-between gap-2 py-0.5 ' + (won ? 'text-[#033871]' : 'text-[#8AA8BC]')}>
                  <span className={'text-xs truncate ' + (won ? 'font-black' : 'font-medium')}>
                    {won && <Trophy className="inline w-3 h-3 text-amber-500 mr-1 -mt-0.5" />}
                    {team.players.join(' / ')}
                  </span>
                  <span className="flex gap-1 shrink-0">
                    {team.sets.map((s, si) => (
                      <span key={si} className={'w-5 h-5 rounded flex items-center justify-center text-[11px] font-black ' + (won ? 'bg-[#0C63BF] text-white' : 'bg-[#F0F7FA] text-[#8AA8BC]')}>{s}</span>
                    ))}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

const TournamentModal = ({ tournament, onClose }) => {
  if (!tournament) return null;
  const cat = tournament.premier_label || tournament.category_main || "Premier Padel";
  const catColor = PREMIER_BADGE_COLORS[cat] || "bg-[#033871] text-white";
  const isPast = (() => {
    // If we already have known winners → it's clearly a past tournament
    if (tournament.winners && (tournament.winners.men || tournament.winners.women)) return true;
    const ref = tournament.end_date || tournament.start_date;
    if (ref) return new Date(ref).getTime() < Date.now();
    if (tournament.publish_date) {
      return new Date(tournament.publish_date).getTime() < Date.now() - 3 * 86400000;
    }
    return false;
  })();

  const formatFullRange = () => {
    if (tournament.start_date && tournament.end_date) {
      const s = new Date(tournament.start_date);
      const e = new Date(tournament.end_date);
      return t('padpro.range', { start: s.toLocaleDateString(loc(), { day: 'numeric', month: 'long', year: 'numeric' }), end: e.toLocaleDateString(loc(), { day: 'numeric', month: 'long', year: 'numeric' }) });
    }
    if (tournament.publish_date) {
      return new Date(tournament.publish_date).toLocaleDateString(loc(), { day: 'numeric', month: 'long', year: 'numeric' });
    }
    return t('padpro.date_tbc');
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center px-4 py-6" data-testid="tournament-modal">
      <div className="absolute inset-0 bg-[#021E47]/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-3xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col">
        {/* Hero */}
        <div className="relative h-56 bg-gradient-to-br from-[#033871] to-[#0C63BF] shrink-0">
          {tournament.image ? (
            <img src={tournament.image} alt={tournament.title} className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center"><Trophy className="w-20 h-20 text-white/30" /></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 hover:bg-white shadow-md flex items-center justify-center text-[#033871]"
            data-testid="modal-close"
          >
            ✕
          </button>
          <div className="absolute top-3 left-3 flex gap-1.5">
            <span className={'px-2.5 py-1 rounded-md text-xs font-black uppercase tracking-wide ' + catColor}>{cat}</span>
            {(tournament.city || tournament.country_main) && (
              <span className="px-2.5 py-1 rounded-md text-xs font-bold uppercase bg-white/85 backdrop-blur text-[#033871]">
                {tournament.city || tournament.country_main}
              </span>
            )}
          </div>
          <h2 className="absolute bottom-4 left-5 right-5 text-2xl sm:text-3xl font-black text-white leading-tight line-clamp-2 drop-shadow-md">
            {tournament.title}
          </h2>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-[#F0F7FA] rounded-xl p-3">
              <p className="text-[10px] uppercase tracking-wide text-[#5A8BA8] font-bold mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> {t('padpro.dates')}</p>
              <p className="text-sm font-bold text-[#033871]">{formatFullRange()}</p>
            </div>
            <div className="bg-[#F0F7FA] rounded-xl p-3">
              <p className="text-[10px] uppercase tracking-wide text-[#5A8BA8] font-bold mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {t('padpro.place')}</p>
              <p className="text-sm font-bold text-[#033871]">
                {tournament.city ? `${tournament.city}${tournament.country_main ? ', ' + tournament.country_main : ''}` : (tournament.country_main || t('padpro.tbc'))}
              </p>
            </div>
            <div className="bg-[#F0F7FA] rounded-xl p-3">
              <p className="text-[10px] uppercase tracking-wide text-[#5A8BA8] font-bold mb-1">{t('padpro.category')}</p>
              <p className="text-sm font-bold text-[#033871]">{cat}</p>
            </div>
            <div className="bg-[#F0F7FA] rounded-xl p-3">
              <p className="text-[10px] uppercase tracking-wide text-[#5A8BA8] font-bold mb-1">{t('padpro.gender')}</p>
              <p className="text-sm font-bold text-[#033871]">
                {(tournament.genders && tournament.genders.length > 0) ? tournament.genders.map(g => g === 'Male' ? t('padpro.men') : g === 'Female' ? t('padpro.women') : t('padpro.mixed')).join(' & ') : t('padpro.tbc')}
              </p>
            </div>
          </div>

          {/* Winners (past Premier Padel events) */}
          {isPast && tournament.winners && (
            <div className="mb-5 rounded-2xl overflow-hidden bg-gradient-to-br from-amber-50 to-amber-100/60 border-2 border-amber-200" data-testid="tournament-winners">
              <div className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white">
                <p className="text-xs font-black uppercase tracking-wide flex items-center gap-1.5">
                  <Trophy className="w-4 h-4" /> {t('padpro.winners')}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-amber-200">
                {tournament.winners.men && (
                  <div className="p-3 sm:p-4">
                    <p className="text-[10px] uppercase tracking-wide text-amber-700 font-bold mb-1.5 flex items-center gap-1">
                      <span className="text-base">♂</span> {t('padpro.men')}
                    </p>
                    <p className="text-sm font-black text-[#033871] leading-snug">
                      {tournament.winners.men[0]}<br/>
                      <span className="text-amber-600 text-xs font-bold">&amp;</span><br/>
                      {tournament.winners.men[1]}
                    </p>
                  </div>
                )}
                {tournament.winners.women && (
                  <div className="p-3 sm:p-4">
                    <p className="text-[10px] uppercase tracking-wide text-pink-700 font-bold mb-1.5 flex items-center gap-1">
                      <span className="text-base">♀</span> {t('padpro.women')}
                    </p>
                    <p className="text-sm font-black text-[#033871] leading-snug">
                      {tournament.winners.women[0]}<br/>
                      <span className="text-pink-500 text-xs font-bold">&amp;</span><br/>
                      {tournament.winners.women[1]}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Match scores from the official Premier Padel API */}
          {isPast && tournament.slug && <ResultsSection slug={tournament.slug} />}

          {tournament.excerpt && (
            <div className="mb-5">
              <p className="text-[10px] uppercase tracking-wide text-[#5A8BA8] font-bold mb-2">{t('padpro.summary')}</p>
              <p className="text-sm text-[#3D5870] leading-relaxed">{tournament.excerpt}</p>
            </div>
          )}

          <div className="bg-gradient-to-br from-[#033871] to-[#0C63BF] rounded-xl p-4 text-white">
            <p className="text-xs font-bold mb-2 flex items-center gap-1"><Trophy className="w-3.5 h-3.5 text-[#32CD32]" /> {t('padpro.play_like_pro')}</p>
            <Link to="/catalogue" onClick={onClose} className="inline-flex items-center gap-1 text-[#32CD32] font-bold text-sm hover:underline">
              {t('padpro.see_circuit_rackets')} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {tournament.link && (
            <a
              href={tournament.link}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 mt-4 text-xs text-[#5A8BA8] hover:text-[#0C63BF]"
            >
              {t('padpro.see_on_pp')} <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const NewsCard = ({ article, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(article)}
    className="text-left bg-white rounded-2xl border border-[#E8F4FD] hover:border-[#0C63BF] hover:shadow-lg transition-all overflow-hidden group w-full"
    data-testid={`news-${article.id}`}
  >
    {article.image ? (
      <div className="aspect-[16/9] overflow-hidden bg-[#F0F7FA]">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      </div>
    ) : (
      <div className="aspect-[16/9] bg-gradient-to-br from-[#033871] to-[#0C63BF] flex items-center justify-center">
        <Newspaper className="w-12 h-12 text-white/40" />
      </div>
    )}
    <div className="p-4">
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#5A8BA8] mb-2">
        <Clock className="w-3 h-3" />{new Date(article.date).toLocaleDateString(loc(), { day: 'numeric', month: 'short', year: 'numeric' })}
      </span>
      <h3 className="text-sm font-bold text-[#033871] leading-snug line-clamp-3 group-hover:text-[#0C63BF] transition-colors mb-2">
        {article.title}
      </h3>
      {article.excerpt && (
        <p className="text-xs text-[#5A8BA8] line-clamp-2">{article.excerpt}</p>
      )}
      <span className="inline-flex items-center gap-1 mt-2 text-xs text-[#0C63BF] font-semibold">
        {t('padpro.read_summary')} <ArrowRight className="w-3 h-3" />
      </span>
    </div>
  </button>
);


const ArticleModal = ({ article, onClose }) => {
  // Body summary is pre-translated on /api/padpro/news so the modal opens
  // INSTANTLY. We only call the per-article endpoint as a fallback if the
  // summary is missing (e.g. legacy cache entry).
  const [details, setDetails] = useState(article && article.summary ? article : null);
  const [loading, setLoading] = useState(!(article && article.summary));

  useEffect(() => {
    if (!article) return;
    if (article.summary) { setDetails(article); setLoading(false); return; }
    let alive = true;
    setLoading(true);
    setDetails(null);
    axios.get(`${API}/padpro/news/${article.id}`, { params: { lang: lang() } })
      .then(r => { if (alive) setDetails(r.data); })
      .catch(() => { if (alive) setDetails(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [article]);

  useEffect(() => {
    if (!article) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handler);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handler);
    };
  }, [article, onClose]);

  if (!article) return null;
  const title = details?.title || article.title;
  const summary = details?.summary || article.excerpt || '';

  return (
    <div className="fixed inset-0 z-[115] flex items-end sm:items-center justify-center px-3 py-4 sm:p-6" data-testid="article-modal">
      <div className="absolute inset-0 bg-[#021E47]/85 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-3xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col">
        <div className="relative h-48 sm:h-60 bg-gradient-to-br from-[#033871] to-[#0C63BF] shrink-0">
          {(details?.image || article.image) ? (
            <img src={details?.image || article.image} alt={title}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center"><Newspaper className="w-16 h-16 text-white/30" /></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 hover:bg-white shadow-md flex items-center justify-center text-[#033871] z-10"
            data-testid="article-modal-close"
          >✕</button>
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wide bg-[#32CD32] text-white">
            <Newspaper className="w-3 h-3" /> Premier Padel
          </span>
          <h2 className="absolute bottom-3 left-4 right-4 text-lg sm:text-2xl font-black text-white leading-tight line-clamp-3 drop-shadow-md">
            {title}
          </h2>
        </div>
        <div className="p-5 sm:p-6 overflow-y-auto">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#5A8BA8] mb-3">
            <Clock className="w-3 h-3" />{new Date(article.date).toLocaleDateString(loc(), { day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
          {loading ? (
            <div className="flex items-center text-[#5A8BA8] text-sm py-6">
              <span className="inline-block w-4 h-4 border-2 border-[#0C63BF]/30 border-t-[#0C63BF] rounded-full animate-spin mr-2" />
              {t('padpro.translating_summary')}
            </div>
          ) : summary ? (
            <p className="text-sm sm:text-base text-[#3D5870] leading-relaxed whitespace-pre-line">{summary}…</p>
          ) : (
            <p className="text-sm text-[#5A8BA8] italic">{t('padpro.summary_unavailable')}</p>
          )}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-[#F0F7FA]">
            <Link
              to="/catalogue" onClick={onClose}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#32CD32] hover:bg-[#28a428] text-white text-xs font-bold rounded-lg"
            >
              {t('padpro.see_pro_rackets')} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <a
              href={article.link} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-[#0C63BF] hover:underline font-semibold"
            >
              {t('padpro.full_article')} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const PadProPage = () => {
  const { i18n: i18nHook } = useTranslation();
  const currentLang = (i18nHook.language || 'fr').slice(0, 2);
  const [activeTab, setActiveTab] = useState('rankings');
  const [gender, setGender] = useState('male');
  const [rankings, setRankings] = useState([]);
  const [tournaments, setTournaments] = useState({ upcoming: [], past: [] });
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const run = async () => {
      try {
        if (activeTab === 'rankings') {
          const res = await axios.get(`${API}/padpro/rankings?gender=${gender}`);
          if (alive) setRankings(res.data.data || []);
        } else if (activeTab === 'tournaments') {
          const res = await axios.get(`${API}/padpro/tournaments`, { params: { lang: currentLang } });
          if (alive) setTournaments(res.data.data || { upcoming: [], past: [] });
        } else if (activeTab === 'news') {
          const res = await axios.get(`${API}/padpro/news`, { params: { lang: currentLang } });
          if (alive) setNews(res.data.data || []);
        }
      } catch (e) { console.error(e); }
      if (alive) setLoading(false);
    };
    run();
    return () => { alive = false; };
  }, [activeTab, gender, currentLang]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F7FAFB] to-[#F0F7FA]">
      <SEO
        title={t('padpro.seo_title')}
        description={t('padpro.seo_desc')}
        path="/padpro"
      />

      {/* HERO */}
      <header className="relative overflow-hidden bg-gradient-to-br from-[#033871] via-[#0C63BF] to-[#033871] text-white">
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '24px 24px' }} />
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#32CD32]/20 blur-3xl" />
        <div className="absolute -bottom-10 -left-10 w-72 h-72 rounded-full bg-[#32CD32]/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 py-12 sm:py-16">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-[#32CD32]" />
            <span className="text-[#32CD32] text-xs font-bold uppercase tracking-widest">PadPro</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-4">
            {t('padpro.hero_1')}<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#32CD32] to-[#5fd88a]">{t('padpro.hero_2')}</span>
          </h1>
          <p className="text-white/75 text-base sm:text-lg max-w-2xl mb-6 font-light">
            {t('padpro.hero_sub')}
          </p>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2" data-testid="padpro-tabs">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={'inline-flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm transition-all ' +
                    (active
                      ? 'bg-[#32CD32] text-white shadow-lg shadow-[#32CD32]/30'
                      : 'bg-white/10 text-white hover:bg-white/20 border border-white/20')}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="w-4 h-4" /> {t(tab.label)}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-10">
        {/* Rankings tab */}
        {activeTab === 'rankings' && (
          <section>
            <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
              <div>
                <h2 className="text-2xl font-black text-[#033871]">{t('padpro.rankings_title')}</h2>
                <p className="text-sm text-[#5A8BA8]">{t('padpro.rankings_sub')}</p>
              </div>
              <div className="flex gap-1 p-1 bg-white border border-[#E8F4FD] rounded-xl" data-testid="gender-toggle">
                <button
                  onClick={() => setGender('male')}
                  className={'px-4 py-1.5 rounded-lg text-sm font-bold transition-all ' + (gender === 'male' ? 'bg-[#0C63BF] text-white' : 'text-[#5A8BA8] hover:bg-[#F0F7FA]')}
                  data-testid="gender-male"
                >{t('padpro.men')}</button>
                <button
                  onClick={() => setGender('female')}
                  className={'px-4 py-1.5 rounded-lg text-sm font-bold transition-all ' + (gender === 'female' ? 'bg-[#0C63BF] text-white' : 'text-[#5A8BA8] hover:bg-[#F0F7FA]')}
                  data-testid="gender-female"
                >{t('padpro.women')}</button>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {[...Array(10)].map((_, i) => <div key={i} className="h-72 bg-white rounded-2xl animate-pulse" />)}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="rankings-grid">
                {rankings.map((p, i) => <PlayerCard key={p.player_id + '-' + i} player={p} idx={i} onClick={setSelectedPlayer} />)}
              </div>
            )}
          </section>
        )}

        {/* Tournaments tab */}
        {activeTab === 'tournaments' && (
          <section>
            <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
              <div>
                <h2 className="text-2xl font-black text-[#033871]">{t('padpro.calendar_title')}</h2>
                <p className="text-sm text-[#5A8BA8]">{t('padpro.calendar_sub')}</p>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-[#5A8BA8] font-bold">{t('padpro.season', { year: new Date().getFullYear() })}</span>
            </div>
            {tournaments.upcoming?.length > 0 && (
              <div className="mb-10">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-4 h-4 text-[#32CD32]" />
                  <h3 className="text-base font-bold text-[#033871]">{t('padpro.upcoming')}</h3>
                </div>
                {groupByMonth(tournaments.upcoming).map(([k, items]) => (
                  <MonthGroup key={k} monthKey={k} items={items} past={false} onClick={setSelectedTournament} />
                ))}
              </div>
            )}
            {tournaments.past?.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Trophy className="w-4 h-4 text-amber-500" />
                  <h3 className="text-base font-bold text-[#033871]">{t('padpro.recent_results')}</h3>
                </div>
                {groupByMonth(tournaments.past).reverse().map(([k, items]) => (
                  <MonthGroup key={k} monthKey={k} items={items} past={true} onClick={setSelectedTournament} />
                ))}
              </div>
            )}
            {!loading && tournaments.upcoming?.length === 0 && tournaments.past?.length === 0 && (
              <p className="text-center text-[#5A8BA8] py-12">{t('padpro.no_tournaments')}</p>
            )}
          </section>
        )}

        {/* News tab */}
        {activeTab === 'news' && (
          <section>
            <h2 className="text-2xl font-black text-[#033871] mb-6">{t('padpro.news_title')}</h2>
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => <div key={i} className="h-80 bg-white rounded-2xl animate-pulse" />)}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="news-grid">
                {news.map(n => <NewsCard key={n.id} article={n} onClick={setSelectedArticle} />)}
              </div>
            )}
          </section>
        )}

        {/* CTA Bridge to marketplace */}
        <div className="mt-16 bg-gradient-to-br from-[#033871] to-[#0C63BF] rounded-3xl p-8 sm:p-10 text-center relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#32CD32]/15 blur-3xl" />
          <div className="relative">
            <Sparkles className="w-8 h-8 text-[#32CD32] mx-auto mb-3" />
            <h2 className="text-2xl sm:text-3xl font-black text-white mb-3">
              {t('padpro.cta_title')}
            </h2>
            <p className="text-white/75 text-base mb-6 max-w-md mx-auto">
              {t('padpro.cta_sub')}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/catalogue">
                <Button className="bg-[#32CD32] hover:bg-[#28a428] text-white font-bold h-12 px-6 gap-1.5">
                  {t('padpro.cta_catalogue')} <ChevronRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/guide">
                <Button variant="outline" className="bg-white/10 hover:bg-white/20 border-white/30 text-white font-bold h-12 px-6">
                  {t('padpro.cta_padcoach')}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-[#5A8BA8] mt-8">
          {t('padpro.data_by')} <a href="https://www.padelfip.com" target="_blank" rel="noreferrer" className="text-[#0C63BF] hover:underline">{t('padpro.fip_name')}</a>. {t('padpro.updated24')}
        </p>
      </main>

      <TournamentModal tournament={selectedTournament} onClose={() => setSelectedTournament(null)} />
      <PlayerDetailModal player={selectedPlayer} onClose={() => setSelectedPlayer(null)} />
      <ArticleModal article={selectedArticle} onClose={() => setSelectedArticle(null)} />
    </div>
  );
};

export default PadProPage;
